import { forwardRef } from 'react'
import LoadingButton, { type LoadingButtonProps } from './LoadingButton'
import Tooltip from './Tooltip'
import { MemberPrivileges } from '@/shared/models/memberModel'
import { useMemberById, usePrivileges } from '@/shared/hooks/useMembers'
import { useTranslations } from 'next-intl'

export type PrivilegesButtonProps = Omit<LoadingButtonProps, 'isLoading'> & {
    isLoading?: boolean
    requiredPrivilege?: MemberPrivileges
    unauthorizedMessage?: string
}

const PrivilegesButton = forwardRef<HTMLButtonElement, PrivilegesButtonProps>(
    ({ requiredPrivilege = 'readwrite', ...props }, ref) => {
        const t = useTranslations('root')

        const member = useMemberById()
        const memberData = member.data

        const { isAuthorized } = usePrivileges({
            member: memberData,
            requiredPrivilege
        })

        const isDisabled = props.disabled || !isAuthorized
        const isLoading = member.isLoading || props.isLoading || false

        const getTooltipMessage = () => {
            if (!isAuthorized) {
                return t('privileges.button')
            }
            if (props.disabled) {
                return props.unauthorizedMessage ?? t('privileges.button')
            }
            return null
        }

        const tooltipMessage = getTooltipMessage()

        return (
            <Tooltip
                isActive={isDisabled && !!tooltipMessage}
                content={tooltipMessage}
                trigger={
                    <LoadingButton
                        {...props}
                        onClick={(e) => {
                            if (isDisabled) {
                                e.preventDefault()
                                return
                            }
                            props.onClick?.(e)
                        }}
                        disabled={isDisabled}
                        isLoading={isLoading}
                        ref={ref}
                        data-testid={props['data-testid'] ?? '__mutation-button__'}
                    >
                        {props.children}
                    </LoadingButton>
                }
            />
        )
    }
)

export default PrivilegesButton
